import { useContext, useState, Dispatch, SetStateAction } from "react";
import { OrderDetail } from "../../../../interfaces/interfaces";
import Button, { ButtonType } from "../../../../components/Button/Button";
import Icon from "../../../../components/Icon/icon";
import LinkButton, { LinkButtonType } from "../../../../components/LinkButton/LinkButton";
import PDFDownloadButton from "../../../../components/PDFDownloadButton/PDFDownloadButton";
import OrderHistoryPDF from "../../../../components/PDFTemplates/Templates/OrderHistoryPDF/OrderHistoryPDF";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { Wrapper } from "../../../../components/styled/styled-components";
import { Context, IContext } from "../../../../context/ContextApp";
import { PaymentOption } from "../../../check-out/payment-selection/sendPaymentSelection";
import { subdomainTypes } from "../../../../context/availableSubdomains";
import { formatFreightOption } from "../util";
import { renderPayment, renderPaymentNZ, ResponseStatusTypes } from "./renders";
import PaymentLinkModal from "../PaymentLinkModal";
import BankTransferInfoModal from "../../../../components/PaymentInfo/BankTransferInfoModal";
import EmailTransferInfoModal from "../../../../components/PaymentInfo/EmailTrasnferInfoModal";
import { AlertContent } from "../../../../interfaces/interfaces";

interface Props {
  order: OrderDetail;
  orderProp: OrderDetail;
  statusBadge: React.ReactNode;
  hasBackOrder: boolean;
  orderId: number;
  responseStatus: ResponseStatusTypes | null;
  paymentSessionUrl: string | null;
  setToast: Dispatch<SetStateAction<AlertContent | null>>;
}

function OrderHistoryHeader({
  order,
  orderProp,
  statusBadge,
  hasBackOrder,
  orderId,
  responseStatus,
  paymentSessionUrl,
  setToast,
}: Props) {
  const { appBranch }: IContext = useContext(Context);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);
  const [isEmailTransferModalOpen, setIsEmailTransferModalOpen] = useState(false);

  const getTrackingUrl = () => {
    const firstTracking = order?.track_numbers?.[0];
    if (firstTracking?.trackingNumber.includes("NOW COURIERS")) {
      const barcode = firstTracking.trackingNumber.substring(13);
      return `https://www.nowcouriers.co.nz/now/servlet/ITNG_TAndTServlet?page=1&VCCA=Enabled&Key_Type=CustomerLabel&customer_number=FASHBIZ&consignment_id=${barcode}`;
    }
    // Accpac sends Purolator URLs without the tracking number in the pin param
    if (appBranch === subdomainTypes.CA && firstTracking?.trackingUrl) {
      try {
        const url = new URL(firstTracking.trackingUrl);
        url.searchParams.set("pin", firstTracking.trackingNumber);
        return url.toString();
      } catch {
        return firstTracking.trackingUrl;
      }
    }
    return firstTracking.trackingUrl;
  };

  const hasTracking = order?.track_numbers && order.track_numbers.length > 0;

  return (
    <>
      {isBankTransferModalOpen && (
        <BankTransferInfoModal onCloseModal={() => setIsBankTransferModalOpen(false)} />
      )}
      {isEmailTransferModalOpen && (
        <EmailTransferInfoModal onCloseModal={() => setIsEmailTransferModalOpen(false)} />
      )}
      <div className="order-details-section">
        <div className="order-info-container">
          <div className="order-info-grid">
            <div className="order-info-item">
              <span className="order-info-label">Submitted</span>
              <span className="order-info-value">{orderProp.submitted_at}</span>
            </div>
            <div className="order-info-item">
              <span className="order-info-label">Status</span>
              <div className="order-info-value">{statusBadge}</div>
            </div>
            <div className="order-info-item">
              <span className="order-info-label">Freight</span>
              <span className="order-info-value">
                {formatFreightOption(orderProp.freight_option) || "N/A"}
              </span>
            </div>
            {order.ship_to_address && (
              <div className="order-info-item">
                <span className="order-info-label">Ship To</span>
                <span className="order-info-value">{order.ship_to_address}</span>
              </div>
            )}
            {hasBackOrder && (
              <div className="order-info-item">
                <span className="order-info-label">Back Order</span>
                <span className="order-info-value backorder-highlight">B/O</span>
              </div>
            )}
          </div>

          {showModal && (
            <PaymentLinkModal orderId={orderId} setShowModal={setShowModal} setToast={setToast} />
          )}
        </div>

        <div className="order-actions-bar">
          <div className="group-buttons">
            {hasTracking && (
              <LinkButton
                to={getTrackingUrl() || "#"}
                target="_blank"
                type={LinkButtonType.secondary}
              >
                <Icon
                  name="ExternalLink"
                  iconWidth="1rem"
                  className="icon--black"
                  style={{ marginRight: "0.5em" }}
                />
                Track Shipment
              </LinkButton>
            )}
            <PDFDownloadButton document={<OrderHistoryPDF order={order} appBranch={appBranch} />}>
              <Button type={ButtonType.secondary}>
                <Icon
                  name="Print"
                  iconWidth="1rem"
                  className="icon--black"
                  style={{ marginRight: "0.5em" }}
                />
                Print
              </Button>
            </PDFDownloadButton>
            {appBranch !== subdomainTypes.NZ &&
              !responseStatus &&
              order.payment_method === PaymentOption.payOnline && (
                <Wrapper style={{ margin: "0 1rem" }}>
                  <LoadingSpinner inline />
                </Wrapper>
              )}
            <div>
              {appBranch !== subdomainTypes.NZ
                ? renderPayment(responseStatus, paymentSessionUrl)
                : renderPaymentNZ(order)}
            </div>
            {order.payment_method === PaymentOption.BankTransfer && (
              <div className="pay-order-button">
                <Button type={ButtonType.primary} onClick={() => setIsBankTransferModalOpen(true)}>
                  Bank Transfer Info
                </Button>
              </div>
            )}
            {order.payment_method === PaymentOption.EmailTransfer && (
              <div className="pay-order-button">
                <Button type={ButtonType.primary} onClick={() => setIsEmailTransferModalOpen(true)}>
                  Bank Transfer Info
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderHistoryHeader;
